// Small shared pieces the Case Sheet sections all use.
//
// Pulled out because eleven sections rendering the same "labelled block with
// a header action", "empty section", "who and when" line would otherwise be
// eleven near-identical implementations that drift.
//
// Four of those pieces — Section, Field, Metric, Empty — and the alert
// severity styling have since been promoted to components/ui/, once the
// Consultation rebuild became their second consumer. They are re-exported
// here rather than having the eleven sections' imports rewritten: this file
// is still the right place for a Case Sheet section to import its chrome
// from, and the definitions now live where a screen outside this feature
// can reach them.
import { useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/hooks/useAuth';
import type { SourceRef } from './types';

export { Section, Field, Metric, Empty } from '@/components/ui/Section';
export { severityClass } from '@/components/ui/AlertStrip';

/**
 * Capability check against the user's module grants.
 *
 * Quick actions and entry forms are ABSENT, not disabled, for a capability the
 * user does not hold — a greyed-out "Order medication" button on a nurse's
 * screen is noise that teaches people to ignore disabled controls.
 */
export function useCan(): (moduleId: string) => boolean {
  const { user } = useAuth();
  const modules = (user as { modules?: string[] } | null)?.modules ?? [];
  return (moduleId: string) => modules.includes(moduleId);
}

// ── Formatting ───────────────────────────────────────────────────

export function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

export function fmtTime(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** "3d 4h" — a length of stay a clinician reads at a glance. */
export function fmtStay(hours: number): string {
  const d = Math.floor(hours / 24);
  const h = Math.round(hours % 24);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

export function fmtAgo(v: string | null | undefined): string {
  if (!v) return '—';
  const mins = Math.round((Date.now() - new Date(v).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

// ── Backdating ───────────────────────────────────────────────────
//
// Every Case Sheet entry form defaults to "now" server-side when no
// timestamp is sent — correct for the overwhelming majority of entries,
// charted as they happen. But a ward is not typed up in real time: a vitals
// round gets written up ten minutes late, a nursing note from an earlier
// shift gets entered after handover, an admission from last night gets its
// assessment filled in the next morning. Without a way to say "this
// actually happened at 6pm, not now", the record ends up timestamped by
// when someone had a free moment to type, not by what actually happened
// when — which corrupts exactly the things this module exists to get
// right: vital trends, medication timing, the timeline's ordering.
//
// Collapsed by default (a button, not a field) so the common case — typing
// in real time — stays a one-line form. Expanding it reveals a
// datetime-local input; collapsing it back (or never touching it) leaves
// the value undefined, which is what makes "server defaults to now" still
// the answer for every caller that doesn't pass one.

/** ISO datetime -> the local, timezone-naive string <input type="datetime-local"> needs. */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The reverse: a datetime-local string (local wall-clock, no zone) -> ISO UTC. */
function localInputToIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local); // browsers parse an un-suffixed "YYYY-MM-DDTHH:mm" as local time
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/**
 * A "when did this actually happen" field for any Case Sheet entry form.
 *
 * `value` is the ISO string that will be sent to the server (undefined =
 * let the server default to now); the input itself always shows/edits local
 * wall-clock time, since that's what a clinician is thinking in when they
 * say "the 6pm round", not UTC.
 */
export function BackdateField({ value, onChange, label = 'When', max }: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  label?: string;
  /** Defaults to "now" — an entry cannot be dated into the future. */
  max?: string;
}) {
  const [expanded, setExpanded] = useState(!!value);
  const maxLocal = isoToLocalInput(max ?? new Date().toISOString());

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="text-[11px] text-ink-muted underline-offset-2 hover:underline"
      >
        🕒 Recorded now — click to backdate
      </button>
    );
  }

  return (
    <div>
      <label className="mb-1 block text-xs font-semibold">{label}</label>
      <div className="flex items-center gap-2">
        <Input
          type="datetime-local"
          className="w-auto"
          max={maxLocal}
          value={value ? isoToLocalInput(value) : maxLocal}
          onChange={(e) => onChange(localInputToIso(e.target.value))}
        />
        <button
          type="button"
          onClick={() => { onChange(undefined); setExpanded(false); }}
          className="text-[11px] text-ink-muted underline-offset-2 hover:underline"
        >
          Use now instead
        </button>
      </div>
    </div>
  );
}

/** Attribution line — who recorded this and when. */
export function Attribution({ who, when, prefix = 'Recorded by' }: { who?: string | null; when?: string | null; prefix?: string }) {
  if (!who && !when) return null;
  return (
    <div className="mt-2 text-[11px] text-ink-muted">
      {prefix} {who || 'unknown'}{when ? ` · ${fmtDateTime(when)}` : ''}
    </div>
  );
}

// ── Clinical status chrome ───────────────────────────────────────

/**
 * A clickable trace to the record a derived statement came from.
 *
 * Rendered wherever the Case Sheet shows something it computed rather than
 * something a clinician typed. `onOpen` receives the ref so the workspace can
 * switch to the right section — the click-through is the point, not decoration.
 */
export function SourceLink({ source, onOpen, label = 'View source' }: {
  source: SourceRef | null; onOpen?: (ref: SourceRef) => void; label?: string;
}) {
  if (!source || !onOpen) return null;
  return (
    <button
      type="button"
      onClick={() => onOpen(source)}
      className="text-[11px] font-medium text-teal underline-offset-2 hover:underline"
    >
      {label}
    </button>
  );
}

/**
 * The order/administration distinction, rendered.
 *
 * `upcoming`/`due`/`missed` describe a SLOT with nothing charted against it —
 * they are never labelled "not given", because nothing charted and a
 * deliberately withheld dose are different facts. This mapping is the UI half
 * of the guarantee the schema and routes enforce.
 */
export const DOSE_STATE_META: Record<string, { label: string; variant: 'neutral' | 'warn' | 'info' | 'success' | 'danger' | 'caution' }> = {
  given: { label: 'Given', variant: 'success' },
  not_given: { label: 'Not given', variant: 'danger' },
  refused: { label: 'Refused', variant: 'danger' },
  held: { label: 'Held', variant: 'caution' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
  upcoming: { label: 'Upcoming', variant: 'neutral' },
  due: { label: 'Due — not charted', variant: 'warn' },
  missed: { label: 'Nothing charted', variant: 'warn' },
};

export function DoseStateBadge({ state }: { state: string }) {
  const meta = DOSE_STATE_META[state] ?? { label: state, variant: 'neutral' as const };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

const ORDER_STATUS_META: Record<string, { label: string; variant: 'neutral' | 'warn' | 'info' | 'success' | 'danger' | 'caution' }> = {
  active: { label: 'Active', variant: 'success' },
  held: { label: 'On hold', variant: 'caution' },
  discontinued: { label: 'Discontinued', variant: 'neutral' },
  completed: { label: 'Completed', variant: 'neutral' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
};

export function OrderStatusBadge({ status }: { status: string }) {
  const meta = ORDER_STATUS_META[status] ?? { label: status, variant: 'neutral' as const };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

export function NoteStatusBadge({ status, source }: { status: string; source?: string }) {
  if (status === 'draft') {
    return (
      <Badge variant={source === 'ai_assisted' ? 'info' : 'warn'}>
        {source === 'ai_assisted' ? 'AI draft — needs review' : 'Draft'}
      </Badge>
    );
  }
  if (status === 'amended') return <Badge variant="neutral">Superseded</Badge>;
  return <Badge variant="success">Signed</Badge>;
}
